import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

/** Ошибка API в формате RFC 9457 (problem+json), как её отдаёт бэкенд. */
export interface FieldError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

const STANDARD_FIELDS = new Set(['type', 'title', 'status', 'code', 'detail', 'errors']);

export class ApiProblem extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string | null;
  readonly errors: FieldError[];
  /** Расширения: `current_version`, `candidates`, `existing`… — зависят от `code`. */
  readonly extra: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    const title = typeof body.title === 'string' ? body.title : 'Ошибка запроса';
    const detail = typeof body.detail === 'string' ? body.detail : null;
    super(detail ?? title);
    this.name = 'ApiProblem';
    this.status = status;
    this.code = typeof body.code === 'string' ? body.code : 'unknown';
    this.title = title;
    this.detail = detail;
    this.errors = Array.isArray(body.errors) ? (body.errors as FieldError[]) : [];
    this.extra = Object.fromEntries(
      Object.entries(body).filter(([key]) => !STANDARD_FIELDS.has(key)),
    );
  }

  /** Текст для пользователя: подробность, если есть, иначе заголовок. */
  get userMessage(): string {
    return this.detail ?? this.title;
  }
}

/** Сеть недоступна, таймаут, ответ не JSON — тоже ApiProblem, чтобы UI обрабатывал одинаково. */
function toProblem(error: AxiosError): ApiProblem {
  const res = error.response;
  if (res && typeof res.data === 'object' && res.data !== null) {
    return new ApiProblem(res.status, res.data as Record<string, unknown>);
  }
  if (res) {
    return new ApiProblem(res.status, { title: `Ошибка сервера (${res.status})`, code: 'server' });
  }
  return new ApiProblem(0, { title: 'Сервер недоступен — проверьте подключение', code: 'network' });
}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => undefined;

/** Что делать при 401 вне входа: сбросить пользователя и отправить на /login (см. auth). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

// Один origin: в dev — прокси Vite, в prod — nginx. Пути в контракте уже с /api/v1.
// Массивы — как ждёт FastAPI: ?status=a&status=b, а не ?status[]=a.
export const http = axios.create({
  withCredentials: true,
  timeout: 30_000,
  paramsSerializer: { indexes: null },
});

http.interceptors.response.use(undefined, (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
  if (axios.isCancel(error)) return Promise.reject(error);
  const problem = toProblem(error);
  const url = error.config?.url ?? '';
  // На /auth/login 401 — «неверный пароль», на /auth/me — просто «не вошёл»: не редиректим.
  if (problem.status === 401 && !url.startsWith('/api/v1/auth/')) onUnauthorized();
  return Promise.reject(problem);
});

/** Мутатор orval: все сгенерированные запросы идут через `http`. */
export async function apiRequest<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> {
  const response = await http.request<T>({ ...config, ...options });
  return response.data;
}

// Сигнатуру с параметром требует orval; ошибки всех эндпоинтов приводятся к ApiProblem.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ErrorType<_E> = ApiProblem;
export type BodyType<B> = B;
